import type { ComponentPropsWithRef } from 'react';
import { cn } from '../lib/cn';
import { VisuallyHidden } from './VisuallyHidden';

export interface SpinnerProps extends Omit<ComponentPropsWithRef<'span'>, 'children'> {
  /**
   * Accessible name announced while the spinner is live.
   *
   * Omit it only when something adjacent already announces the wait — a button
   * carrying `aria-busy`, or a region with its own `aria-live`. Without a
   * label the spinner renders `aria-hidden`, because a nameless `role="status"`
   * is an empty announcement rather than a helpful one.
   */
  label?: string | undefined;
  size?: 'sm' | 'md' | undefined;
}

const SIZES = {
  sm: 'size-3.5 border-[1.5px]',
  md: 'size-5 border-2',
} as const;

export function Spinner({ label, size = 'md', className, ...props }: SpinnerProps) {
  const spinner = (
    <span
      className={cn(
        'inline-block animate-spin rounded-full border-current border-t-transparent',
        SIZES[size],
        className,
      )}
    />
  );

  if (label === undefined) {
    return (
      <span aria-hidden {...props}>
        {spinner}
      </span>
    );
  }

  return (
    <span role="status" {...props}>
      {spinner}
      <VisuallyHidden>{label}</VisuallyHidden>
    </span>
  );
}
